import { Chat } from "@/types/chat"
import {
  FolioDocumentSource,
  FolioDocumentTypeGroup,
} from "@/types/folio/folio-document"
import {
  ItineraryEvent,
  ItineraryEventFolioDefaultTrip,
} from "@/types/itinerary"
import { TripRef, TripUpdate } from "@/types/trip"
import pluralize from "pluralize"

import { buildClientAxios } from "@/lib/client-axios"
import { FolioDocumentApi } from "@/lib/folio-document-api"
import folioDocumentSlice from "@/lib/storage/folio-document-slice"
import { toastManager } from "@/lib/toast"
import { addDays, browserTimeZone, browserToday } from "./date-utils"
import { displayAirportCity } from "./flight-utils"
import { formatPlaceLocale } from "./format-utils"
import { AppDispatch } from "./storage/redux-store"

interface FlightRouteDisplay {
  isNonStop: boolean
  origin: string
  destination: string
}

export function folioDefaultTripToTripUpdate(
  defaultTrip: ItineraryEventFolioDefaultTrip
): TripUpdate {
  return {
    destination_entity_ref: defaultTrip.destination_entity_ref,
    major_stops: defaultTrip.major_stops,
    time_frame: {
      start_date: defaultTrip.draft_start_date,
      duration: defaultTrip.draft_duration,
    },
    travelers: defaultTrip.travelers,
  }
}

export function emptyChat(): Chat {
  // The point of the empty chat is to create a stand-in.
  // That's why we are setting the date range to be very inclusive.
  const today = addDays(browserToday(), -365 * 10)
  const duration = 20 * 365
  return {
    id: 0,
    group: false,
    messages: [],
    log_items: [],
    more_messages: false,
    stream_ids: [],
    trip: {
      id: 0,
      ref: "t-abcd1234",
      owner_ref: "u-fake",
      user_refs: ["u-fake"],
      collaboration: false,
      destination_name: undefined,
      user_created: false,
      time_frame: {
        start_date: today,
        duration: duration,
      },
      draft_start_date: today,
      draft_duration: duration,
      time_zone: browserTimeZone(),
      travelers: {
        adult_count: 1,
        child_count: 0,
        infant_count: 0,
        pet_count: 0,
      },
      rooms: [
        {
          adult_count: 1,
          child_ages: [],
          pet_count: 0,
        },
      ],
      roadtrip: false,
    },
  }
}

export const VALID_FOLIO_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "text/plain",
  "text/html",
]

export const ACCEPTED_FOLIO_EXTENSIONS = ".pdf,.jpeg,.jpg,.png,.htm,.html,.txt"

export const validateFolioFileType = (
  file: File,
  showToast = true
): boolean => {
  if (!VALID_FOLIO_FILE_TYPES.includes(file.type)) {
    if (showToast) {
      toastManager.add({
        type: "error",
        description: "Only PDF, image, and text files are allowed.",
      })
    }
    return false
  }
  return true
}

export const formatEventType = (type: string, plural = false): string => {
  const str = type?.replace(/_/g, " ") || ""
  return plural ? pluralize(str) : str
}

export const handleFolioDocumentUpload = async (
  files: File[],
  tripRef: TripRef | null | undefined,
  source: FolioDocumentSource,
  dispatch: AppDispatch,
  onSuccess?: () => void
): Promise<boolean> => {
  const folioDocumentApi = new FolioDocumentApi(buildClientAxios())

  const invalidFiles = files.filter(
    (file) => !validateFolioFileType(file, false)
  )
  if (invalidFiles.length > 0) {
    toastManager.add({
      type: "error",
      description: `${invalidFiles.length} invalid ${pluralize(
        "receipt",
        invalidFiles.length
      )} detected. Please upload only screenshots or PDFs.`,
    })
    return false
  }

  try {
    const res = await folioDocumentApi.upload(tripRef, source, files)
    const fileWord = files.length === 1 ? "receipt is" : "receipts are"
    toastManager.add({
      type: "success",
      description: `Your ${fileWord} being processed. We’ll notify you when ${files.length === 1 ? "it’s" : "they’re"} ready.`,
    })
    if (res.folio_document_id && tripRef)
      dispatch(
        folioDocumentSlice.actions.addUploadingFolioDocument({
          tripRef,
          folioDocumentId: res.folio_document_id,
        })
      )
    onSuccess?.()
    return true
  } catch (error) {
    console.error("Receipt upload failed:", error)
    toastManager.add({
      type: "error",
      description: `Failed to upload ${pluralize("receipt", files.length)}. Please try again.`,
    })
    return false
  }
}

export function formatEventTitle(
  event: ItineraryEvent,
  isMultiEvent = false,
  events: ItineraryEvent[] = []
): string {
  if (event.type === "flight") {
    if (isMultiEvent) {
      const allSameAirline = events.every(
        (e) => e.type === "flight" && e.airline?.name === event.airline?.name
      )
      return allSameAirline
        ? event.airline?.name ||
            event.properties.folio_document_props?.event_data?.title ||
            event.title
        : event.properties.folio_document_props?.event_data?.title ||
            event.title
    }
    return (
      event.airline?.name ||
      event.properties.folio_document_props?.event_data?.title ||
      event.title
    )
  }
  // The event's own title, so a name the traveler edits shows; it starts as the receipt's.
  return (
    event.title ||
    event.properties.folio_document_props?.event_data?.title ||
    ""
  )
}

/**
 * A Bookings row's heading. A Mindtrip flight is titled by its whole itinerary ("Round Trip: Los Angeles
 * (LAX) to New York City (LGA)"), as the booking row carries it; any other group by its first event.
 */
export function formatTypeGroupTitle(typeGroup: FolioDocumentTypeGroup) {
  const { booking, events } = typeGroup
  if (booking?.source_type === "FlightBooking" && booking.title) {
    return booking.title
  }
  return formatEventTitle(events[0], events.length > 1, events)
}

/**
 * The line under a Bookings row's heading. A Mindtrip flight's route is in its title, so its line is
 * the airline when every leg shares one; any other group describes its route or place.
 */
export function formatTypeGroupDescription(
  typeGroup: FolioDocumentTypeGroup
): string | FlightRouteDisplay {
  const { booking, events } = typeGroup
  if (booking?.source_type === "FlightBooking" && booking.title) {
    const airline = events[0]?.airline?.name
    if (airline && events.every((event) => event.airline?.name === airline)) {
      return airline
    }
  }
  return formatEventDescription(events[0], events[events.length - 1], events)
}

export function formatEventDescription(
  earliest: ItineraryEvent,
  latest: ItineraryEvent,
  events: ItineraryEvent[]
): string | FlightRouteDisplay {
  if (
    earliest?.place?.type === "airport" &&
    latest?.end_place?.type === "airport"
  ) {
    if (earliest.place.code === latest.end_place.code) {
      if (events.length === 2 && earliest.end_place?.type === "airport") {
        return `Round-trip to ${displayAirportCity(earliest.end_place)}`
      }
      return `Round-trip from ${displayAirportCity(earliest.place)}`
    }

    return {
      isNonStop: events.length === 1,
      origin: earliest.place.code,
      destination: latest.end_place.code,
    }
  } else if (
    earliest?.type === "transit" &&
    earliest?.place &&
    latest?.end_place
  ) {
    return {
      isNonStop: events.length === 1,
      origin: earliest.place.name,
      destination: latest.end_place.name,
    }
  }

  return latest?.place ? formatPlaceLocale(latest.place) : ""
}
